/**
 * Public SMS terms and SMS privacy pages for one office:
 * `/b/<slug>/sms-terms` and `/b/<slug>/privacy`.
 *
 * Carriers and The Campaign Registry check, during A2P 10DLC campaign review,
 * that the brand publishes the program name, message types, frequency, "Msg &
 * data rates may apply", STOP / HELP instructions, a support contact, and a
 * privacy policy saying mobile numbers and SMS opt-in data are not shared with
 * third parties for marketing. The opt-in wording itself is the server's
 * versioned text (site.smsConsent), so the page and the checkbox never drift.
 *
 * DRAFT wording for the practice and counsel to approve before the page is
 * linked from a live campaign (docs/compliance/a2p-10dlc-checklist.md).
 */
import type { PublicSite } from '@velora/contracts/booking';
import { formatPhoneInput } from '../lib/format.js';

export type PolicyPage = 'sms-terms' | 'privacy';

/** `/b/<slug>/sms-terms` or `/b/<slug>/privacy`, else null (the booking flow). */
export function policyPageFromPath(pathname: string): PolicyPage | null {
  const m = /^\/b\/[^/?#]+\/(sms-terms|privacy)\/?$/.exec(pathname);
  return (m?.[1] as PolicyPage | undefined) ?? null;
}

function addressText(site: PublicSite): string {
  const a = site.address;
  return [a.line1, [a.city, a.state].filter(Boolean).join(', '), a.postalCode]
    .filter(Boolean)
    .join(' ');
}

export function SmsPolicy({ page, site }: { page: PolicyPage; site: PublicSite }) {
  const brand = site.practiceName;
  const phone = site.phone ? formatPhoneInput(site.phone) : null;
  const address = addressText(site);
  const contact = (
    <>
      {brand}
      {address ? `, ${address}` : ''}
      {phone ? `. Phone ${phone}` : ''}.
    </>
  );
  const terms = site.smsConsent?.termsUrl ?? `/b/${site.slug}/sms-terms`;
  const privacy = site.smsConsent?.privacyUrl ?? `/b/${site.slug}/privacy`;

  if (page === 'sms-terms') {
    return (
      <article className="bw-card bw-policy" data-testid="sms-terms">
        <h1>{brand} text message terms</h1>
        <p className="bw-dim">Program name: {brand} patient text messages.</p>

        <h2>What we text</h2>
        <p>
          Appointment reminders and confirmations, recall notices when you are due for a visit,
          replies to messages you send us, follow-ups to missed calls, and links to forms and
          statements. We send offers or practice news only if you separately agreed to marketing
          texts.
        </p>

        <h2>How you sign up</h2>
        <p>
          You agree to texts by ticking the text message box when you book online, on your new
          patient or update forms, by telling our front desk (we record the date, time and staff
          member), or by texting us first. Consent is not a condition of treatment.
        </p>
        {site.smsConsent ? (
          <>
            <p>The wording you agree to when you book online:</p>
            <blockquote className="bw-policy__quote">
              <strong>{site.smsConsent.transactional.label}</strong>
              <br />
              {site.smsConsent.transactional.text}
            </blockquote>
            <blockquote className="bw-policy__quote">
              <strong>{site.smsConsent.marketing.label}</strong>
              <br />
              {site.smsConsent.marketing.text}
            </blockquote>
          </>
        ) : null}

        <h2>Frequency and cost</h2>
        <p>
          Message frequency varies with your appointments, usually a few messages a month. Message
          and data rates may apply.
        </p>

        <h2>Stop, start and help</h2>
        <ul>
          <li>
            Reply <strong>STOP</strong> (or UNSUBSCRIBE, CANCEL, END, QUIT, REVOKE, OPT OUT) to stop
            all texts. We send one message confirming you are unsubscribed.
          </li>
          <li>
            Reply <strong>START</strong> to receive texts again.
          </li>
          <li>
            Reply <strong>HELP</strong> for help, or call us{phone ? ` at ${phone}` : ''}.
          </li>
        </ul>

        <h2>Your health information</h2>
        <p>
          Texts carry only what is needed, such as the day and time of your visit. We do not text
          diagnoses or treatment details. Text messages are not encrypted end to end; call us if you
          would rather not receive a particular message by text.
        </p>

        <h2>Carriers</h2>
        <p>Mobile carriers are not liable for delayed or undelivered messages.</p>

        <h2>Contact</h2>
        <p>{contact}</p>
        <p>
          <a href={privacy}>Privacy policy for text messages</a>
        </p>
      </article>
    );
  }

  return (
    <article className="bw-card bw-policy" data-testid="sms-privacy">
      <h1>{brand} privacy policy for text messages</h1>
      <p className="bw-dim">
        This policy covers text messages. How we use and protect your health information is
        described in our Notice of Privacy Practices, available at the front desk.
      </p>

      <h2>What we collect</h2>
      <p>
        Your mobile number, your text message consent (when and how you gave it, and the wording you
        agreed to), opt-outs, and the messages we exchange.
      </p>

      <h2>How we use it</h2>
      <p>
        To send the messages you asked for, to answer your messages, and to keep a record of your
        consent as the law and mobile carriers require.
      </p>

      <h2>Sharing</h2>
      <p>
        <strong>
          We do not sell, rent or share your mobile number or text message opt-in data and consent
          with third parties or affiliates for their marketing or promotional purposes.
        </strong>{' '}
        We share it only with the service providers that deliver our messages (our text message
        provider and phone carriers), under agreements that protect it, or when the law requires.
      </p>

      <h2>Keeping it safe</h2>
      <p>
        Access is limited to our staff and service providers who need it. Consent records are kept
        for at least five years.
      </p>

      <h2>Your choices</h2>
      <p>
        Reply STOP to any text to stop receiving texts, or tell our front desk. Reply HELP for help.
        See our <a href={terms}>text message terms</a>.
      </p>

      <h2>Contact</h2>
      <p>{contact}</p>
    </article>
  );
}
